<template>
  <div v-if="loaded" class="account-settings-container">
    <div class="content">
      <div class="title">
        {{ isEdit ? $t('editPackage') : $t('addPackage') }}
      </div>
      <div class="column">
        <div class="container">
          <div class="form-container">
            <div class="input-title">{{ $t('packageName') }}</div>
            <input v-model="name" type="text" class="input" :placeholder="$t('suggestPackageName')" />
          </div>
          <div class="form-container">
            <div class="input-title">{{ $t('packagePriceMoralPerson') }}</div>
            <input v-model="pricePm" type="number" min="0" class="input" :placeholder="$t('suggestPackagePrice')" />
          </div>
          <div class="form-container">
            <div class="input-title">{{ $t('packagePricePhysicPerson') }}</div>
            <input v-model="pricePp" type="number" min="0" class="input" :placeholder="$t('suggestPackagePrice')" />
          </div>
          <div class="form-container">
            <div class="input-title">{{ $t('packageCode') }}</div>
            <input v-model="code" type="text" class="input" :placeholder="$t('suggestPackageCode')" />
          </div>
        </div>
        <div class="container">
          <div class="form-container">
            <div class="input-title">{{ $t('documents') }}</div>
            <div class="input checkbox">
              <div v-for="document in documents" :key="document.type" class="check-box" @click="checkedDocuments ^= document.value">
                <div class="checkable" :class="{ checked: (checkedDocuments & document.value) != 0 }" />
                <div class="value">
                  {{ document.type }}
                </div>
              </div>
            </div>
          </div>

          <div v-if="!isEdit" class="form-container">
            <div class="input-title">{{ $t('subscriberAccounts') }}</div>
            <Dropdown
              class="monitoring-dropdown"
              :config="{ inputSize: 48, color: 'white', active: false }"
              :values="[{ name: $t('chooseSubscribedAccount') }].concat(companies)"
              :placeholder="$t('chooseSubscribedAccount')"
              :on-change="value => (selectedCompany = value)"
              :value="selectedCompany"
            >
              <template #placeholder="{ value, isPlaceholder }">
                <div v-if="!isPlaceholder" class="manager">
                  {{ value.name }}
                </div>
                <div v-else class="manager grey">{{ value }}</div>
              </template>
              <template #choices="{ choice }">
                <div class="manager white" :class="{ highlight: choice === selectedCompany }">
                  {{ choice.name }}
                </div>
              </template>
            </Dropdown>
          </div>
          <div v-else class="form-container">
            <div class="input-title">{{ $t('subscriberAccounts') }}</div>
            <input type="text" class="input disable" :value="selectedCompany && selectedCompany.name ? selectedCompany.name : ''" :placeholder="''" disabled />
          </div>
        </div>
      </div>
      <div v-if="$route.query.isEnabled" class="info">
        <div class="text black pointer" @click.stop="disablePackageLevel">
          {{ $t('disablePackage') }}
        </div>
      </div>
      <div class="btn-container">
        <div class="main-button cancel" @click="$router.go(-1)">
          {{ $t('cancel') }}
        </div>
        <div class="main-button" @click="() => (isEdit ? edit() : add())">
          {{ isEdit ? $t('change') : $t('add') }}
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import Dropdown from '@/components/Dropdown';

export default {
  components: { Dropdown },

  beforeRouteLeave(to, from, next) {
    if (this.isSend) return next();
    if (
      ((this.name && this.name !== this.$route.query.name) ||
        (this.pricePm && this.pricePm !== this.$route.query.pricePm) ||
        (this.pricePp && this.pricePp !== this.$route.query.pricePp) ||
        (this.label && this.label !== this.$route.query.label) ||
        (this.checkedDocuments && this.checkedDocuments !== this.$route.query.checkedDocuments)) &&
      confirm(this.$t('areYouSureToExit'))
    ) {
      return next();
    } else if (
      !(this.name && this.name !== this.$route.query.name) &&
      !(this.pricePm && this.pricePm !== this.$route.query.pricePm) &&
      !(this.pricePp && this.pricePp !== this.$route.query.pricePp) &&
      !(this.label && this.label !== this.$route.query.label) &&
      !(this.checkedDocuments && this.checkedDocuments !== this.$route.query.checkedDocuments)
    ) {
      return next();
    }
  },

  props: {
    isEdit: {
      type: Boolean,
      required: true,
    },
  },

  data() {
    return {
      loaded: false,
      name: '',
      pricePm: 0,
      pricePp: 0,
      code: '',
      label: '',
      companies: [],
      package: null,
      selectedCompany: '',
      documents: [
        { type: this.$t('documentTypes.kbis'), value: 1 },
        { type: this.$t('documentTypes.actes'), value: 2 },
        { type: this.$t('documentTypes.bilan'), value: 4 },
        { type: this.$t('documentTypes.beneficiary'), value: 8 },
      ],
      checkedDocuments: 0,
      errorDuringDisabling: false,
    };
  },

  computed: {
    ...mapGetters(['user']),

    getManagerInfos() {
      const info = this.users.find(value => value.id === this.manager);
      return info;
    },
  },

  async mounted() {
    this.companies = await this.$store.dispatch('getClients', '');
    if (this.$route.query.id) this.getPackages();
    this.loaded = true;
  },

  methods: {
    async add() {
      await this.$store.dispatch('postPackage', {
        name: this.name,
        packageCombinaisonValue: parseInt(this.checkedDocuments),
        clientPublicId: this.selectedCompany.id,
        pricePm: parseFloat(this.pricePm),
        pricePp: parseFloat(this.pricePp),
        code: this.code,
      });
      this.isSend = true;
      this.$router.go(-1);
    },

    async edit() {
      await this.$store.dispatch('patchPackage', {
        id: this.$route.query.id,
        name: this.name,
        packageCombinaisonValue: parseInt(this.checkedDocuments),
        pricePm: parseFloat(this.pricePm),
        pricePp: parseFloat(this.pricePp),
        code: this.code,
      });
      this.isSend = true;
      this.$router.go(-1);
    },

    async getPackages() {
      this.name = this.$route.query.name;
      this.pricePp = this.$route.query.pricePp;
      this.pricePm = this.$route.query.pricePm;
      this.label = this.$route.query.label;
      this.checkedDocuments = this.$route.query.checkedDocuments;
      this.selectedCompany = this.companies.find(e => e.id === this.$route.query.companyId);
    },

    async disablePackageLevel() {
      if (await this.$store.dispatch('setPopup', { popupType: 'Confirm' })) {
        await this.$store.dispatch('patchPackage', {
          id: this.$route.query.id,
          isEnabled: !this.$route.query.isEnabled,
        });
        this.isSend = true;
        this.$router.go(-1);
      }
    },
  },
};
</script>

<style scoped lang="scss">
@use '@/styles/colors.scss';
@use '@/styles/responsive.scss';
@use '@/styles/fonts.scss';
.account-settings-container {
  margin: 60px 0 0;

  .content {
    .column {
      display: flex;
    }
    .title {
      font-family: fonts.$title-font;
      color: colors.$darkblue-color;
      font-weight: 500;
      font-size: 22px;
      text-align: left;
    }
    .container {
      display: flex;
      align-items: flex-start;
      justify-content: flex-start;
      flex-direction: column;
      flex-grow: 1;
      margin-right: 24px;

      &:nth-child(2n) {
        margin-left: 24px;
        margin-right: 0px;
      }
    }

    .form-container {
      width: 100%;
      display: flex;
      flex-direction: column;
      align-items: flex-start;
      justify-content: center;

      .input-title {
        color: colors.$darkblue-color;
        font-weight: 500;
        font-size: 16px;
        margin: 46px 0 10px 0;

        &.low-margin {
          margin: 20px 0 10px 0;
        }
      }

      .input {
        width: 100%;
        max-width: 100%;
        height: 48px;
        margin: 0 0 16px;
        padding: 12px 16px;
        box-sizing: border-box;
        color: colors.$text-color;
        border: solid 1px colors.$lightgrey-color;
        border-radius: 4px;
        background: colors.$white-color;

        &.disable {
          cursor: not-allowed;
          background: rgba(colors.$lightgrey-color, 0.15);
        }

        &.checkbox {
          display: flex;
          align-items: center;
          justify-content: space-around;
        }

        &:last-child {
          margin: 0;
        }

        &::placeholder {
          color: colors.$lightgrey-color;
        }

        .check-box {
          cursor: pointer;
          display: flex;
          align-items: center;
          flex-grow: 1;
          transition: all 0.3s;

          .value {
            margin-left: 16px;
          }

          .checkable {
            position: relative;
            display: flex;
            align-items: center;
            justify-content: center;
            $size: 16px;
            width: $size;
            height: $size;
            border-radius: 4px;
            border: solid 1px colors.$lightgrey-color;
            transition: 0.2s ease-out;

            &::after {
              display: block;
              position: absolute;
              content: '';
              border: solid white;
              border-width: 0 2px 2px 0;
              margin: -1px 0 0;
              padding: 2px 2px 8px 2px;
              opacity: 0;
              transform: scale(0.6);
              transition: 0.2s ease-out;
            }

            &.checked {
              border-color: colors.$main-color;
              background: colors.$main-color;

              &::after {
                opacity: 1;
                transform: rotate(45deg) scale(1);
              }
            }
          }
        }
      }
    }

    .btn-container {
      display: flex;
      align-items: center;
      justify-content: center;
      margin: 80px 0 24px;

      .main-button {
        margin: 0 12px 0;
        width: 200px;
        padding: 0;

        &:hover {
          cursor: pointer;
        }

        &.cancel {
          margin: 0 12px 0 0;
          background: colors.$lightgrey-color;
          color: colors.$darkgrey-color;
        }
      }
    }
    .info {
      display: flex;
      align-items: center;
      margin: 14px 0 0 0;
      font-size: 14px;

      &.margin-top {
        margin: 64px 0 0;
        flex-direction: column;
        align-items: flex-start;

        .error {
          color: colors.$red-color;
          margin-bottom: 16px;
        }
      }

      .icon {
        margin: 0 12px 0 0;
        position: relative;
        width: 18px;
        height: 18px;
        border-radius: 50%;
        border: 2px solid colors.$lightblue-color;
        flex-shrink: 0;

        &:after {
          position: absolute;
          content: '';
          bottom: 3px;
          left: calc(50% - 1px);
          right: calc(50% - 1px);
          height: 8px;
          background: colors.$lightblue-color;
        }

        &:before {
          position: absolute;
          content: '';
          top: 3px;
          left: calc(50% - 1px);
          right: calc(50% - 1px);
          height: 2px;
          background: colors.$lightblue-color;
        }
      }

      .text {
        color: colors.$lightblue-color !important;
        font-size: 14px;
        opacity: 0.8;
        &.black {
          color: colors.$black-color;
          margin-bottom: 14px;
          font-weight: 500;
          font-size: 14px;
          line-height: 16px;
          display: flex;
          align-items: center;
          text-decoration-line: underline;

          &.pointer {
            cursor: pointer;
          }
        }
      }
    }
  }
}
</style>
